import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as env from 'config/environment';
import * as consts from 'config/consts';
import { useRouting } from 'hooks/useRouting';
import { formatDate } from 'utils/formatter';
import { initializeScrolTop } from 'utils/dom';
import { categoryNames } from 'pages/NotificationList';
import Loading from 'components/commons/loading/Loading';
import Icon from 'components/commons/icon/Icon';
import Footer from 'components/commons/Footer';
import { HeadBlock } from './components/HeadBlock';
import LoginSideMenu from './components/LoginSideMenu';
import { NotificationItems } from './Login';

function autoLink(content: string): string {
  const regexpUrl = /(?<url>https?:\/\/[-_.!~*'()a-zA-Z0-9;/?:@&=+$,%#]+)(\[:b (?<text>.*)\]){0,1}/g;

  return content.replace(regexpUrl, (...args) => {
    const groups = args.pop();
    const url = groups['url'];
    const text = groups['text'];

    // 画像は特定の拡張子のみ画像表示されるようにする
    if (
      url.indexOf('.jpg') !== -1 ||
      url.indexOf('.jpeg') !== -1 ||
      url.indexOf('.png') !== -1 ||
      url.indexOf('.gif') !== -1 ||
      url.indexOf('.svg') !== -1
    ) {
      return `<img src="${url}" alt="お知らせ画像" class="mx-auto" />`;
    }

    const target = url.startsWith(env.appURL) ? '' : 'target="_blank"';
    if (text) {
      return `<div class="ui-button theme-outline my-4 mx-4 p-2"><a class="text-link" href="${url}" ${target}>${text}</a></div>`;
    } else {
      return `<a class=${'text-link'} href="${url}" ${target}>${url}</a>`;
    }
  });
}

function createContent(
  content: string | undefined
): {
  __html: string;
} {
  return { __html: autoLink(unescape(content ?? '')) };
}

export const BreadcrumbList = (
  list: { name: string; path?: string }[],
  style?: string
) => {
  return (
    <div
      className={`flex flex-row items-center mt-4 text-xs overflow-visible ${style}`}
      style={{
        whiteSpace: 'nowrap',
        maskImage: 'linear-gradient(to right, black 80%, transparent 97%)',
      }}>
      {list.map((item, i) => {
        return (
          <React.Fragment key={i}>
            {item.path ? (
              <>
                <div className="text-blue-700">
                  <a href={item.path}>{item.name}</a>
                </div>
                <div className="icon-chevron-right ml-1 text-bluegray-400" />
              </>
            ) : (
              <div className="ml-1 text-bluegray-700 truncate">{item.name}</div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const PostNotificationListDetail: React.FC = () => {
  const { notificationID } = useParams<{
    notificationID: string;
  }>();

  // お知らせ一覧情報
  const [notificationItems, setNotificationItems] = useState<
    NotificationItems[] | undefined
  >(undefined);
  // お知らせ詳細
  const [targetNotification, setTargetNotification] = useState<
    NotificationItems | undefined
  >(undefined);

  const headers = {
    Accept: 'application/json',
  };

  const [isShowSideMenu, setIsShowSideMenu] = useState(false);
  const [hideAnimate, setHideAnimate] = useState<boolean>(false);

  const menuShow = (): void => {
    setIsShowSideMenu(true);
    setHideAnimate(false);
  };
  const menuHide = (): void => {
    setIsShowSideMenu(false);
  };
  const menuHideAnimate = (): void => {
    setHideAnimate(true);
  };

  const headerRightActionButtons = (): JSX.Element | null => {
    return (
      <div className={`flex items-center`}>
        <section className={`pl-4 pr-2 pt-2`}>
          <div
            className={`btn-trigger ${isShowSideMenu ? 'active' : ''}`}
            id="btn01"
            onClick={!isShowSideMenu ? menuShow : menuHideAnimate}>
            <span />
            <span />
            <span />
          </div>
        </section>
      </div>
    );
  };

  useEffect(() => {
    document.domain === 'localhost'
      ? (async () => {
          const res = await fetch(
            `https://stg.tw.minkabu.jp/status/notifications.json`,
            {
              cache: 'no-cache',
              method: 'GET',
              headers,
            }
          );
          if (res.status === 200) {
            const data_ = (await res.json()) as NotificationItems[];
            const data = data_.filter(
              data => data.BeforeLogin === consts.NotificationCode.投稿
            );
            setNotificationItems(data);
            setTargetNotification(
              data.find(items => items.ID === notificationID)
            );
          }
        })()
      : (async () => {
          const res = await fetch(`${env.appURL}/status/notifications.json`, {
            cache: 'no-cache',
            method: 'GET',
            headers,
          });
          if (res.status === 200) {
            const data_ = (await res.json()) as NotificationItems[];
            const data = data_.filter(
              data => data.BeforeLogin === consts.NotificationCode.投稿
            );
            setNotificationItems(data);
            setTargetNotification(
              data.find(items => items.ID === notificationID)
            );
          }
        })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chkTarget = (shiftNum: number): number => {
    const invalid = -1;
    if (!notificationItems) return invalid;

    let targetIndex = notificationItems.findIndex(
      item => item.ID === targetNotification?.ID
    );
    if (targetIndex === invalid) return invalid;

    // シフトさせた位置にレコードが存在すれば、その位置を返却
    targetIndex += shiftNum;
    if (targetIndex >= 0 && targetIndex <= notificationItems.length - 1) {
      return targetIndex;
    }
    return invalid;
  };

  const getNotificationDetail = (shiftNum: number): void => {
    initializeScrolTop();
    if (!notificationItems) return;

    const targetIndex = chkTarget(shiftNum);
    if (targetIndex === -1) return;

    // 表示対象IDを参照
    const newReadID = notificationItems[targetIndex].ID;

    const newNotifications = notificationItems.find(
      item => item.ID === newReadID
    );
    setTargetNotification(newNotifications);
  };

  const routing = useRouting();
  const goBack = (): void => {
    routing.replaceRouting({ path: `/postNotificationList` }, true);
  };

  const bannerList = useMemo(() => {
    return [
      <>
        <a
          href="https://tw.minkabu.jp/"
          target="_blank"
          rel="noopener noreferrer">
          <img
            src="/static/images/post-notification/asepura_banner.svg"
            alt="株式・投資信託をまとめて管理"
          />
        </a>
      </>,
      <>
        <a
          href="https://play.google.com/store/apps/details?id=jp.minkabu.ap"
          target="_blank"
          rel="noopener noreferrer">
          <img
            src="/static/images/post-notification/google_play_banner.svg"
            alt="Google Playで手に入れよう"
          />
        </a>
      </>,
    ];
  }, []);

  if (!notificationItems)
    return (
      <Loading
        loading={true}
        style={{
          backgroundColor: '#edf2f7',
          alignItems: 'normal',
          paddingTop: '200px',
          top: '95',
          left: '0',
        }}
      />
    );

  if (targetNotification === undefined) {
    return <>お知らせが見つかりません。</>;
  }

  const title = unescape(targetNotification.Title ?? '');
  const breadcrumbInfo = [
    { name: 'アセプラTOP', path: '/' },
    { name: 'お知らせ一覧', path: '/postNotificationList' },
    { name: title },
  ];

  const headBlockValue = {
    title:
      '配当管理、資産管理におすすめ！リアルな投資家データを公開｜みんかぶアセットプランナー（アセプラ）',
    ogTitle:
      '配当管理、資産管理におすすめ！リアルな投資家データを公開｜みんかぶアセットプランナー（アセプラ）',
    description:
      '資産形成のお悩みをユーザアンケートからご紹介！延べ13万人以上のリアルな投資家データも公開中。あなたの配当管理、資産管理をサポートします。投資分析ならみんかぶアセットプランナー。',
    ogDescription:
      '資産形成のお悩みをユーザアンケートからご紹介！延べ13万人以上のリアルな投資家データも公開中。あなたの配当管理、資産管理をサポートします。投資分析ならみんかぶアセットプランナー。',
    img: 'https://tw.minkabu.jp/static/images/lp/walk-img-4.png',
  };

  return (
    <>
      <HeadBlock
        {...{
          title: headBlockValue.title,
          ogTitle: headBlockValue.ogTitle,
          description: headBlockValue.description,
          ogDescription: headBlockValue.ogDescription,
          img: headBlockValue.img,
        }}
      />
      <div className="flex flex-col scrollable h-full">
        <div className="flex-grow">
          <header
            className="bg-gray-100 border-b-2 border-gray-300"
            id="id_header">
            <nav className="container flex p-2">
              <div
                className="flex-1 flex justify-start"
                style={{ minWidth: '72px' }}>
                <Link to="/postNotificationList">
                  <button className="flex items-center p-2">
                    <Icon
                      name="chevron-left"
                      className="text-2xl text-gray-700"
                    />
                  </button>
                </Link>
              </div>

              <h1 className="font-bold text-center p-2 truncate-420">
                お知らせ詳細
              </h1>
              <div className="flex-1 flex justify-end">
                {headerRightActionButtons()}
              </div>
            </nav>
            {isShowSideMenu && (
              <LoginSideMenu
                hideAnimate={hideAnimate}
                menuHide={menuHide}
                onNavLinkClick={(e: React.MouseEvent, path: string) => {
                  return;
                }}
              />
            )}
          </header>

          <div className="container" id="id_container">
            {BreadcrumbList(breadcrumbInfo, 'px-3')}
            <div className="bg-white rounded m-3 p-3">
              <div className="flex text-gray-500 text-sm p-2">
                <div className="">
                  {formatDate(
                    new Date(
                      targetNotification.UpdTime
                        ? targetNotification.UpdTime
                        : targetNotification.InsTime
                    ),
                    'yyyy/MM/dd HH:mm'
                  )}
                </div>
                <div className="ml-6">
                  {categoryNames[targetNotification.Type]}
                </div>
              </div>
              <div className="break-all p-2">
                <div className="font-bold">{title}</div>
                <div
                  className="mt-4 whitespace-pre-wrap"
                  dangerouslySetInnerHTML={createContent(
                    targetNotification.Content
                  )}
                />
              </div>

              <div className="flex p-2">
                <div
                  className={`ui-prev-next ${
                    chkTarget(-1) === -1
                      ? 'ui-prev-next-disabled'
                      : 'cursor-pointer'
                  }`}
                  onClick={(): void => getNotificationDetail(-1)}>
                  前
                </div>
                <div className="flex-grow"></div>
                <div
                  className={`ui-prev-next ${
                    chkTarget(1) === -1
                      ? 'ui-prev-next-disabled'
                      : 'cursor-pointer'
                  }`}
                  onClick={(): void => getNotificationDetail(1)}>
                  次
                </div>
              </div>
              <div className="ui-border-b pt-2" />
              <div className="p-2">
                <div className="text-link cursor-pointer" onClick={goBack}>
                  お知らせ一覧へ戻る
                </div>
              </div>
            </div>
          </div>
          <div className="container hidden lg:flex flex-row items-center justify-center mt-8">
            <div className="mx-2">{bannerList[0]}</div>
            <div className="mx-2">{bannerList[1]}</div>
          </div>
          <div className="container lg:hidden flex flex-col items-center justify-center mt-8">
            <div className="mx-2">{bannerList[0]}</div>
            <div className="mx-2 mt-3">{bannerList[1]}</div>
          </div>
        </div>
        <Footer showFooterMarginTop={true} />
      </div>
    </>
  );
};

export default PostNotificationListDetail;
